import { useLazyQuery, useQuery } from "@apollo/client";
import { compact, isEqual, range } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GetOrganizationTalentIndicatorsQuery,
  GetOrganizationTalentIndicatorsQueryVariables,
  GetTalentIndicatorTrendsQuery,
  GetTalentIndicatorTrendsQueryVariables,
  TrendReportGroupBy,
  TrendReportType,
} from "types/graphql-schema";

import getOrganizationTalentIndicatorsQuery from "@apps/org-settings/graphql/get-organization-talent-indicators-query";
import { currentOrganizationVar } from "@cache/cache";
import Button, { ButtonSize } from "@components/button/button";
import Loading from "@components/loading/loading";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import getTalentIndicatorTrendQuery from "../graphql/get-talent-indicator-trend-query";
import AvgLineChart from "./trend-report/avg-line-chart";
import { TrendReportFilterType } from "./trend-report/filters/types";
import StackedDistributionChart from "./trend-report/stacked-distribution-chart";
import TrendReportViewOptionsSidebar, {
  TrendReportDataType,
  TrendReportViewOptions,
} from "./trend-report/trend-report-view-options-sidebar";

const TrendReportPage = () => {
  const organization = currentOrganizationVar();
  const [showViewOptionsSidebar, setShowViewOptionsSidebar] = useState(false);
  const [viewOptions, setViewOptions] = useState<TrendReportViewOptions>({
    name: "View 1",
    dataType: undefined,
    reportType: TrendReportType.Distribution,
    groupBy: TrendReportGroupBy.Organization,
    filters: [],
  });

  const handleUpdateViewOptions = useCallback(
    (newViewOptions: TrendReportViewOptions) => {
      if (isEqual(viewOptions, newViewOptions)) {
        return;
      }
      setViewOptions(newViewOptions);
    },
    [viewOptions]
  );

  const { data: talentIndicatorsData, loading: isLoadingTalentIndicators } =
    useQuery<
      GetOrganizationTalentIndicatorsQuery,
      GetOrganizationTalentIndicatorsQueryVariables
    >(getOrganizationTalentIndicatorsQuery, {
      variables: {
        organizationId: organization.id,
      },
      onCompleted: (data) => {
        const indicators = assertEdgesNonNull(data.talentIndicators);
        if (indicators.length > 0) {
          setViewOptions((prev) => ({
            ...prev,
            dataType: {
              type: TrendReportDataType.TALENT_INDICATOR,
              talentIndicatorId: indicators[0].id,
            },
          }));
        }
      },
    });

  const [getTrendData, { loading: isLoadingTrendData, data: trendData }] =
    useLazyQuery<
      GetTalentIndicatorTrendsQuery,
      GetTalentIndicatorTrendsQueryVariables
    >(getTalentIndicatorTrendQuery, {
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (!viewOptions.dataType) {
      return;
    }
    const teamFilter = viewOptions.filters.find(
      (t) => t.type === TrendReportFilterType.TEAM
    );
    const managerFilter = viewOptions.filters.find(
      (t) => t.type === TrendReportFilterType.MANAGER
    );
    getTrendData({
      variables: {
        organizationId: organization.id,
        talentIndicatorId: viewOptions.dataType.talentIndicatorId,
        reportType: viewOptions.reportType,
        groupBy:
          viewOptions.reportType === TrendReportType.Average
            ? viewOptions.groupBy
            : undefined,
        startDate: viewOptions.startDate,
        endDate: viewOptions.endDate,
        teamIds: teamFilter ? teamFilter.data.map((t) => t.id) : undefined,
        managerIds: managerFilter
          ? managerFilter.data.map((t) => t.id)
          : undefined,
      },
    });
    // only want to fetch data when viewOptions changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewOptions]);

  const talentIndicators = useMemo(
    () =>
      talentIndicatorsData?.talentIndicators
        ? assertEdgesNonNull(talentIndicatorsData.talentIndicators)
        : [],
    [talentIndicatorsData]
  );

  const selectedTalentIndicator = useMemo(() => {
    return viewOptions.dataType
      ? talentIndicators.find(
          ({ id }) => id === viewOptions.dataType?.talentIndicatorId
        )
      : null;
  }, [talentIndicators, viewOptions]);
  const selectedTalentIndicatorLabels = useMemo(() => {
    if (!selectedTalentIndicator) {
      return [];
    }
    return range(
      selectedTalentIndicator.scaleStartsAt,
      selectedTalentIndicator.scaleStartsAt + selectedTalentIndicator.scale
    ).reduce((acc, x, index) => {
      return {
        ...acc,
        [x]:
          selectedTalentIndicator.labels?.[index] ??
          `${index + selectedTalentIndicator.scaleStartsAt}`,
      };
    }, {} as Record<number, string>);
  }, [selectedTalentIndicator]);

  const parsedTrendData = useMemo(() => {
    return trendData?.trendReport
      ? compact(
          trendData?.trendReport.map((dataNode) => {
            if (!dataNode) {
              return null;
            }
            const node: any = {
              name: dataNode.name,
            };
            const counts: any = {};
            dataNode.data.forEach((segment) => {
              node[assertNonNull(segment?.key)] = assertNonNull(segment?.value);
              counts[assertNonNull(segment?.key)] = assertNonNull(
                segment?.count
              );
            });
            return { ...node, counts, labels: selectedTalentIndicatorLabels };
          })
        )
      : [];
  }, [selectedTalentIndicatorLabels, trendData]);

  if (isLoadingTalentIndicators || isLoadingTrendData) {
    return (
      <div className="flex-1 flex justify-center p-10">
        <Loading>Loading...</Loading>
      </div>
    );
  }

  return (
    <div className="w-full px-8">
      {showViewOptionsSidebar && (
        <TrendReportViewOptionsSidebar
          talentIndicators={talentIndicators}
          viewOptions={viewOptions}
          onClose={() => setShowViewOptionsSidebar(false)}
          onUpdateViewOptions={handleUpdateViewOptions}
        />
      )}

      <div className="mb-6 flex items-center justify-between">
        <div></div>
        <div className="flex items-center gap-4">
          <Button
            disabled={false}
            className="flex items-center ml-4"
            size={ButtonSize.large}
            onClick={() => setShowViewOptionsSidebar(!showViewOptionsSidebar)}
          >
            <TopicflowIcon type="adjustments" className="h-5 w-5 mr-1" />
            View Options
          </Button>
        </div>
      </div>

      {selectedTalentIndicator && trendData?.trendReport && (
        <div className="p-6 bg-gray-50 rounded-md">
          <div className="flex justify-center h-[620px]">
            {viewOptions.reportType === TrendReportType.Distribution && (
              <StackedDistributionChart
                data={parsedTrendData}
                talentIndicatorName={
                  selectedTalentIndicator.customName &&
                  selectedTalentIndicator.customName.trim() !== ""
                    ? selectedTalentIndicator.customName
                    : selectedTalentIndicator.name
                }
              />
            )}
            {viewOptions.reportType === TrendReportType.Average && (
              <AvgLineChart
                data={parsedTrendData}
                startValue={selectedTalentIndicator.scaleStartsAt}
                endValue={
                  selectedTalentIndicator.scaleStartsAt +
                  selectedTalentIndicator.scale -
                  1
                }
                talentIndicatorName={
                  selectedTalentIndicator.customName &&
                  selectedTalentIndicator.customName.trim() !== ""
                    ? selectedTalentIndicator.customName
                    : selectedTalentIndicator.name
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendReportPage;
